import type { CSSProperties } from 'react';

/**
 * Single source of truth for the workflow-status badge — the pill rendered in
 * the Status column of every study list in the app: PACS, Worklist, Orders,
 * Dispatch, Front Desk, the QC workstation, the patient-orders modal and the
 * Order Info order history.
 *
 * Every one of those lists used to carry its own copy of this switch, so the
 * same status rendered in four different colors and three different font sizes
 * depending on which screen you were looking at. Import this instead of adding
 * another local copy.
 *
 * The reporting side panels (Prior Reports / Report History) reuse these colors
 * in a smaller chip — see pages/Reporting/components/Panels/statusStyles.
 *
 * Two rules drive the palette:
 *
 * 1. The reporting lifecycle reads as a traffic light, so the column can be
 *    scanned without reading the words:
 *        UNREPORTED red → DRAFTED yellow → REVIEWED orange → SIGNEDOFF green.
 *    Statuses off that path (NEW, NOT READY, CANCELLED) stay neutral/blue so
 *    those four colors keep their meaning — in particular NEW is blue, not
 *    red: red belongs to the unreported backlog alone.
 *
 * 2. The fills are SOLID, not tints. The grids zebra-stripe rows white/#D9EDF3
 *    and tint emergency rows red and ASAP rows amber; a pale pill disappears
 *    into those rows. Text color is whichever of white / near-black carries on
 *    its own fill (dark on the light yellow and orange, white on the dark red,
 *    green, slate and blue), and each pill keeps a 1px border a shade darker
 *    than its fill so it still has an edge on a tinted row.
 */

/** Shared geometry + typography. Every badge in every table gets exactly this. */
const BASE_STYLE: CSSProperties = {
  height: '22px',
  borderRadius: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontWeight: 700,
  fontSize: '13px',
  lineHeight: '22px',
  letterSpacing: '0.02px',
  verticalAlign: 'middle',
  borderStyle: 'solid',
  borderWidth: '1px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
  minWidth: '58px',
  margin: 0,
  textAlign: 'center',
  // Labels are single words ("UNREPORTED", "NOT READY") — never let a narrow
  // column wrap a badge onto two lines; the column minWidths fit the widest one.
  whiteSpace: 'nowrap',
};

type BadgeColors = Pick<CSSProperties, 'background' | 'color' | 'borderColor'>;

const STATUS_COLORS: Record<string, BadgeColors> = {
  // Rendered as "UNREPORTED" in the scan lists (raw "SCANNED" on Orders). The
  // red matches the emergency row accent (#d32029) the grids already use.
  SCANNED: { background: '#D32029', color: '#FFFFFF', borderColor: '#A4141C' },
  DRAFTED: { background: '#FACC15', color: '#3F2A00', borderColor: '#D4A906' },
  REVIEWED: { background: '#FB8C00', color: '#3D1F00', borderColor: '#D26F00' },
  SIGNEDOFF: { background: '#0E8A54', color: '#FFFFFF', borderColor: '#066B40' },
  // Off the reporting path — neutral, so neither can be mistaken for the red
  // unreported backlog.
  NOT_FOR_REPORTING: { background: '#475467', color: '#FFFFFF', borderColor: '#344054' },
  CANCELLED: { background: '#8A93A5', color: '#FFFFFF', borderColor: '#6E7688' },
  // "NEW" — ordered, nothing scanned yet.
  PENDING: { background: '#1570EF', color: '#FFFFFF', borderColor: '#0E56BC' },
};

const DEFAULT_COLORS: BadgeColors = STATUS_COLORS.PENDING;

/**
 * Inline style for a status badge. Spread it onto the <Tag>; per-site tweaks
 * (cursor, click affordances) go after the spread — but not colors or sizes,
 * or the tables drift apart again.
 */
export const getStatusBadgeStyle = (status?: string | null): CSSProperties => ({
  ...BASE_STYLE,
  ...(STATUS_COLORS[(status || '').toUpperCase()] || DEFAULT_COLORS),
});

/** Legacy name kept so the existing call sites read unchanged. */
export const getStatusStyle = getStatusBadgeStyle;
